import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** The slice of the video the timeline is currently drawing. */
export interface TimelineView {
  /** Seconds at the left edge. */
  start: number
  /** Seconds covered by the full width. */
  span: number
}

/** Tightest window we allow. Half a second across ~800 px is sub-frame
 * precision at 60 fps — past that, zooming stops buying accuracy. */
const MIN_SPAN_S = 0.5

/** Wheel-delta → zoom factor. A notch is ~100 px of deltaY, giving
 * exp(0.15) ≈ 1.16× per notch: gradual enough to land on a frame, fast
 * enough to cross a two-hour video in a few flicks. Trackpads send much
 * smaller deltas and get a proportionally smoother ramp. */
const ZOOM_SENSITIVITY = 0.0015

/** Fraction of the window a shift-wheel notch pans. */
const PAN_STEP = 0.15

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}

export interface TimelineViewApi {
  view: TimelineView
  /** True whenever the window is narrower than the whole video. */
  zoomed: boolean
  /** Zoom level as a multiplier of the full duration (1 = fit). */
  scale: number
  /** Zoom keeping the time under ``ratio`` (0–1 of the width) pinned in
   * place — the gesture users expect from a map. */
  zoomAt: (ratio: number, deltaY: number) => void
  /** Shift the window by a fraction of its own span. */
  panBy: (spanFraction: number) => void
  /** Move the window so ``time`` sits at ``ratio`` across the width. */
  centerOn: (time: number, ratio?: number) => void
  reset: () => void
  /** Time under a 0–1 position across the timeline's width. */
  timeAt: (ratio: number) => number
  /** Left offset in %, for absolutely positioning a marker at ``time``.
   * Can fall outside 0–100 when the marker is off-screen; the timeline
   * clips it. */
  percentOf: (time: number) => number
  /** Seconds represented by one pixel — drag math needs this. */
  secondsPerPixel: (widthPx: number) => number
}

/**
 * Zoom/pan state for the timeline.
 *
 * The window is kept in *seconds* rather than a zoom factor + offset
 * because every consumer (markers, playhead, drags, ticks) asks the same
 * two questions — "what time is at x?" and "where does time t sit?" —
 * and seconds answer both without a second conversion step.
 */
export function useTimelineView(duration: number): TimelineViewApi {
  const [view, setView] = useState<TimelineView>({ start: 0, span: 0 })

  // Fit the whole video whenever the source changes (new video, metadata
  // arriving late). Zoom is a per-video reading aid, not a saved setting.
  useEffect(() => {
    setView({ start: 0, span: duration })
  }, [duration])

  const durationRef = useRef(duration)
  durationRef.current = duration

  const minSpan = useMemo(
    () => (duration > 0 ? Math.min(MIN_SPAN_S, duration) : MIN_SPAN_S),
    [duration],
  )

  const zoomAt = useCallback(
    (ratio: number, deltaY: number) => {
      const total = durationRef.current
      if (total <= 0) return
      setView((prev) => {
        const span = prev.span > 0 ? prev.span : total
        const anchor = prev.start + clamp(ratio, 0, 1) * span
        const nextSpan = clamp(span * Math.exp(deltaY * ZOOM_SENSITIVITY), minSpan, total)
        // Keep the anchored time under the cursor, then clamp the window
        // into the video — near the edges the anchor drifts, which is the
        // same behaviour every map/NLE has.
        const nextStart = clamp(anchor - clamp(ratio, 0, 1) * nextSpan, 0, total - nextSpan)
        return { start: nextStart, span: nextSpan }
      })
    },
    [minSpan],
  )

  const panBy = useCallback((spanFraction: number) => {
    const total = durationRef.current
    if (total <= 0) return
    setView((prev) => {
      const span = prev.span > 0 ? prev.span : total
      return { start: clamp(prev.start + span * spanFraction, 0, total - span), span }
    })
  }, [])

  const centerOn = useCallback((time: number, ratio = 0.5) => {
    const total = durationRef.current
    if (total <= 0) return
    setView((prev) => {
      const span = prev.span > 0 ? prev.span : total
      return { start: clamp(time - span * ratio, 0, total - span), span }
    })
  }, [])

  const reset = useCallback(() => {
    setView({ start: 0, span: durationRef.current })
  }, [])

  const span = view.span > 0 ? view.span : duration
  const timeAt = useCallback(
    (ratio: number) => view.start + clamp(ratio, 0, 1) * (view.span || duration),
    [view, duration],
  )
  const percentOf = useCallback(
    (time: number) => {
      const width = view.span || duration
      if (width <= 0) return 0
      return ((time - view.start) / width) * 100
    },
    [view, duration],
  )
  const secondsPerPixel = useCallback(
    (widthPx: number) => (widthPx > 0 ? (view.span || duration) / widthPx : 0),
    [view, duration],
  )

  return {
    view,
    zoomed: duration > 0 && span > 0 && span < duration - 0.01,
    scale: span > 0 ? duration / span : 1,
    zoomAt,
    panBy,
    centerOn,
    reset,
    timeAt,
    percentOf,
    secondsPerPixel,
  }
}

export { PAN_STEP }
